import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import { CheckCircle, Mail } from '@mui/icons-material';
import OtpInput from './OtpInput';
import { initializeTab } from '../../lib/auth/tabManager'

declare global {
  interface Window {
    initSendOTP: (config: any) => void;
    sendOtp: (
      email: string,
      successCallback: (data: any) => void,
      errorCallback: (error: any) => void,
    ) => void;
    verifyOtp: (
      otp: number,
      successCallback: (data: any) => void,
      errorCallback: (error: any) => void,
      requestId?: string,
    ) => void;
    retryOtp: (
      channel: any,
      successCallback: (data: any) => void,
      errorCallback: (error: any) => void,
      requestId?: string,
    ) => void;
  }
}

interface OtpVerificationProps {
  onVerificationSuccess: () => void;
}

const OtpVerification = ({
  onVerificationSuccess,
}: OtpVerificationProps) => {
  const { accounts } = useMsal();
  const email = accounts?.[0]?.username || '';

  const widgetId = import.meta.env.VITE_MSG91_WIDGET_ID;
  const tokenAuth = import.meta.env.VITE_MSG91_TOKEN_AUTH;

  const [isInitialized] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(() => {
    return localStorage.getItem('i_ov') === 'true';
  });
  const [otp, setOtp] = useState('');
  const [retryTimer, setRetryTimer] = useState(0);
  const [redirectTimer, setRedirectTimer] = useState(0);
  const [otpError, setOtpError] = useState<string | null>(null);

  useEffect(() => {
    initializeTab();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'i_ov') {
        if (event.newValue === 'true') {
        
          onVerificationSuccess();
        } else if (event.newValue === 'false') {
          setIsVerified(false);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setIsVerified(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onVerificationSuccess]);

  const maskEmail = (email: string) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    if (!username || !domain) return email;

    let maskedUsername = '';
    if (username.length === 1) {
      maskedUsername = username;
    } else if (username.length === 2) {
      maskedUsername = `${username.charAt(0)}*`;
    } else if (username.length <= 4) {
      maskedUsername = `${username.charAt(0)}${'*'.repeat(username.length - 1)}`;
    } else {
      maskedUsername = `${username.substring(0, 2)}${'*'.repeat(username.length - 2)}`;
    }

    return `${maskedUsername}@${domain}`;
  };

  const configuration = {
    widgetId: widgetId,
    tokenAuth: tokenAuth,
    identifier: email,
    exposeMethods: true,
    success: (data: any) => {
      console.log('Configuration success response: ', data);
      setIsVerified(true);
      localStorage.setItem('i_ov', 'true');
      showToast('Your email has been verified successfully.', 'success');
    },
    failure: (error: any) => {
      console.log('Configuration failure: ', error);
      showToast(error?.message || 'Please try again', 'error');
    },
    otp: '<otp>',
  };

  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showToast = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning',
  ) => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const startRetryTimer = () => {
    setRetryTimer(60);
    const timer = setInterval(() => {
      setRetryTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = () => {
    if (!window.initSendOTP) {
      showToast('Service Unavailable', 'error');
      return;
    }

    window.initSendOTP(configuration);
    setIsVerifying(true);
    startRetryTimer();
    showToast('OTP Sent', 'info');
  };

  const handleVerifyOtp = () => {
    setOtpError(null);

    if (!otp || otp.length !== 6) {
      setOtpError('Invalid OTP. Please enter a 6-digit code.');
      return;
    }

    if (!window.verifyOtp) {
      setOtpError('Service unavailable. Please try again later.');
      return;
    }

    window.verifyOtp(
      parseInt(otp),
      data => {
        console.log('OTP verified:', data);
        setIsVerified(true);
        localStorage.setItem('i_ov', 'true');

        

        setRedirectTimer(3);
        const timer = setInterval(() => {
          setRedirectTimer(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              onVerificationSuccess();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        showToast('Verification Successful', 'success');
      },
      error => {
        console.log('Verify OTP Error: ', error);
        setOtpError(error?.message ? error.message.toUpperCase() : 'Invalid OTP. Please try again.');
        setOtp('');
      },
    );
  };

  const handleRetryOtp = () => {
    if (retryTimer > 0) return;

    if (!window.retryOtp) {
      showToast('Service Unavailable', 'error');
      return;
    }

    window.retryOtp(
      '3',
      data => {
        console.log('OTP resent:', data);
        startRetryTimer();
        showToast('OTP Resent', 'info');
      },
      error => {
        console.log('Retry OTP Error: ', error);
        showToast(error?.message || 'Failed to Resend OTP', 'error');
      },
    );
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      p: 2
    }}>
      <Card sx={{ width: '100%', maxWidth: 'md', boxShadow: 3 }}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Mail sx={{ fontSize: 48, color: 'primary.main', mr: 2, mt: 0.5 }} />
            <Typography variant="h4" color="primary" sx={{ mb: 0 }}>
              Email Verification
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {isVerified
              ? 'Your email has been verified'
              : isVerifying
                ? 'Enter the OTP sent to your email'
                : 'Verify your email to access EasyData'}
          </Typography>
          {!isVerifying && (
            <Typography>
              OTP will be sent to {maskEmail(email)}
            </Typography>
          )}
        </Box>
        <CardContent>
          {isVerified ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 3,
              }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="text.primary">
                Verification Complete
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {maskEmail(email)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Redirecting to EasyData{redirectTimer > 0 ? ` in ${redirectTimer} seconds...` : '...'}
              </Typography>
            </Box>
          ) : isVerifying ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {otpError && (
                <Typography variant="body2" color="error" align="center" sx={{ mb: 1 }}>
                  {otpError}
                </Typography>
              )}
              <OtpInput length={6} value={otp} onChange={setOtp} />
              <Typography variant="body2" color="text.secondary" align="center">
                {retryTimer > 0 ? `Retry in ${retryTimer} seconds` : ''}
              </Typography>
              <Box
                sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={otp.length !== 6}>
                  Verify OTP
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRetryOtp}
                  disabled={retryTimer > 0}>
                  Resend OTP
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSendOtp}
                disabled={!isInitialized}>
                Send OTP
              </Button>
            </Box>
          )}
        </CardContent>
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={handleCloseToast} severity={toast.severity}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Card>
    </Box>
  );
};

export default OtpVerification;